// Implements: F-VER-004, F-VER-010, F-VER-016, F-UIX-013
/**
 * "Exportar" (HTML, ZIP, diagram ZIP) of a mockup's version, through the client: a 403 or a 404
 * becomes a toast with the server's sentence, never an error body saved as a file. Without an
 * explicit version (the gallery card), the mockup's current version is read from `GET
 * /mockups/{id}` under `mockupDetailKey`; a mockup with no version says so and downloads nothing.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { asApiError } from "../../../shared/api/asApiError";
import { fetchMockupDetail } from "../../../shared/api/mockupDetail";
import { mockupDetailKey } from "../../../shared/api/queryKeys";
import { downloadVersionFile } from "../../../shared/api/versionDownloads";
import { useTranslation } from "../../../shared/i18n";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { saveBlob } from "../../../shared/ui/saveBlob";
import type { VersionDownload } from "../../../types/api";

/** The name of a file whose answer carried none (the server always names it). */
const FALLBACK_NAMES: Readonly<Record<VersionDownload, string>> = {
  html: "prototipo.html",
  zip: "prototipo.zip",
  diagram: "diagrama.zip",
};

export function useExportMockupVersion(): (
  mockupId: string,
  kind: VersionDownload,
  versionId?: string | null,
) => Promise<void> {
  const { t } = useTranslation();
  const { push } = useToast();
  const queryClient = useQueryClient();

  return useCallback(
    async (mockupId: string, kind: VersionDownload, versionId?: string | null): Promise<void> => {
      try {
        const target =
          versionId ??
          (
            await queryClient.query({
              queryKey: mockupDetailKey(mockupId),
              queryFn: () => fetchMockupDetail(mockupId),
            })
          ).current_version_id;
        if (target === null) {
          push({ tone: "info", message: t("mockups.export.noVersion") });
          return;
        }
        const file = await downloadVersionFile(mockupId, target, kind);
        saveBlob(file.blob, file.filename ?? FALLBACK_NAMES[kind]);
      } catch (cause) {
        push({ tone: "danger", message: userMessage(asApiError(cause), t("mockups.export.failed")) });
      }
    },
    [push, queryClient, t],
  );
}
